// frontend/src/modules/rescisao/pages/Rescisoes.js
import React, { useState, useEffect } from 'react';
import { FaPlus, FaEye, FaPrint, FaEdit, FaTrash, FaCalculator, FaCheck, FaSearch, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

function Rescisoes({ usuario }) {
  const empresaId = usuario?.empresa_id || 1;
  const navigate = useNavigate();
  const [rescisoes, setRescisoes] = useState([]);
  const [rescisoesFiltradas, setRescisoesFiltradas] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFuncionario, setSearchFuncionario] = useState('');
  const [formData, setFormData] = useState({
    funcionario_id: '',
    data_saida: '',
    data_aviso_previo: '',
    motivo: '',
    observacao: ''
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const token = localStorage.getItem('token');

  // ============================================
  // BUSCAR RESCISÕES - URL QUE FUNCIONA
  // ============================================
  const fetchRescisoes = async () => {
    setLoading(true);
    setError('');
    try {
      // MANTENDO A URL ORIGINAL QUE FUNCIONA
      const url = `/api/empresa/${empresaId}/rescisoes`;
      console.log('📝 Buscando rescisões:', url);
      
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Rescisões carregadas:', data.length);
      setRescisoes(data);
      setRescisoesFiltradas(data);
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // BUSCAR FUNCIONÁRIOS
  // ============================================
  const fetchFuncionarios = async () => {
    try {
      const response = await fetch(`/api/empresa/${empresaId}/funcionarios`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar funcionários');
      const data = await response.json();
      setFuncionarios(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchRescisoes();
    fetchFuncionarios();
  }, []);

  // ============================================
  // BUSCA NA LISTA
  // ============================================
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term.trim() === '') {
      setRescisoesFiltradas(rescisoes);
      return;
    }

    const filtradas = rescisoes.filter(r => {
      const nomeFuncionario = r.funcionario?.nome_completo?.toLowerCase() || '';
      const motivo = (r.motivo || '').toLowerCase();
      const status = (r.status || '').toLowerCase();
      const dataSaida = r.data_saida ? new Date(r.data_saida).toLocaleDateString('pt-BR') : '';

      return nomeFuncionario.includes(term) ||
             motivo.includes(term) ||
             status.includes(term) ||
             dataSaida.includes(term);
    });

    setRescisoesFiltradas(filtradas);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setRescisoesFiltradas(rescisoes);
  };

  // ============================================
  // FORMULÁRIO
  // ============================================
  const resetForm = () => {
    setFormData({
      funcionario_id: '',
      data_saida: '',
      data_aviso_previo: '',
      motivo: '',
      observacao: ''
    });
    setSearchFuncionario('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/rescisao/calcular', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          funcionario_id: parseInt(formData.funcionario_id)
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao calcular rescisão');
      }

      setSuccess('✅ Rescisão calculada com sucesso!');
      setMostrarForm(false);
      resetForm();
      fetchRescisoes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EXCLUIR
  // ============================================
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta rescisão?')) return;
    try {
      const response = await fetch(`/api/rescisao/rescisao/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao excluir');
      setSuccess('✅ Rescisão excluída com sucesso!');
      fetchRescisoes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================================
  // UTILITÁRIOS
  // ============================================
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getMotivoLabel = (motivo) => {
    const motivos = {
      'DEMISSAO_SEM_JUSTA_CAUSA': 'Demissão sem Justa Causa',
      'DEMISSAO_COM_JUSTA_CAUSA': 'Demissão com Justa Causa',
      'PEDIDO_DEMISSAO': 'Pedido de Demissão',
      'ACORDO': 'Acordo',
      'FIM_CONTRATO': 'Fim de Contrato',
      'APOSENTADORIA': 'Aposentadoria'
    };
    return motivos[motivo] || motivo;
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDENTE': '#fff3cd',
      'CALCULADO': '#cce5ff',
      'PAGO': '#d4edda',
      'CANCELADO': '#f8d7da'
    };
    return colors[status] || '#e9ecef';
  };

  const getStatusTextColor = (status) => {
    const colors = {
      'PENDENTE': '#856404',
      'CALCULADO': '#004085',
      'PAGO': '#155724',
      'CANCELADO': '#721c24'
    };
    return colors[status] || '#6c757d';
  };

  const funcionariosFiltrados = funcionarios.filter(f => 
    f.ativo && f.nome_completo.toLowerCase().includes(searchFuncionario.toLowerCase())
  );

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando...</div>;

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>📋 Rescisões Contratuais</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Gerencie as rescisões de contratos de trabalho
          </p>
        </div>
        <button 
          onClick={() => { setMostrarForm(!mostrarForm); if (!mostrarForm) resetForm(); }}
          style={{ 
            padding: isMobile ? '8px 16px' : '10px 20px',
            backgroundColor: mostrarForm ? '#dc3545' : '#28a745', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer',
            fontSize: isMobile ? '13px' : '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {mostrarForm ? <FaTimes /> : <FaPlus />} 
          {mostrarForm ? 'Cancelar' : 'Nova Rescisão'}
        </button>
      </div>

      {/* Mensagens */}
      {error && <div style={{ backgroundColor: '#ffebee', padding: '10px', borderRadius: '4px', color: '#c62828', marginBottom: '15px' }}>❌ {error}</div>}
      {success && <div style={{ backgroundColor: '#e8f5e9', padding: '10px', borderRadius: '4px', color: '#2e7d32', marginBottom: '15px' }}>✅ {success}</div>}

      {/* Barra de Busca */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '12px' : '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: isMobile ? '150px' : '250px' }}>
            <input
              type="text"
              placeholder="🔍 Buscar por funcionário, motivo, status..."
              value={searchTerm}
              onChange={handleSearch}
              style={{
                width: '100%',
                padding: isMobile ? '8px 12px' : '10px 15px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: isMobile ? '13px' : '14px'
              }}
            />
          </div>
          {searchTerm && (
            <button onClick={handleClearSearch} style={{ padding: isMobile ? '6px 12px' : '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '12px' : '14px' }}>
              <FaTimes /> Limpar
            </button>
          )}
          <span style={{ marginLeft: 'auto', color: '#6c757d', fontSize: isMobile ? '12px' : '14px' }}>
            {rescisoesFiltradas.length} rescisão(ões)
          </span>
        </div>
      </div>

      {/* Formulário */}
      {mostrarForm && (
        <form onSubmit={handleSubmit} style={{ backgroundColor: '#f8f9fa', padding: isMobile ? '15px' : '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📝 Nova Rescisão</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
            <div>
              <label>Funcionário *</label>
              <input
                type="text"
                placeholder="🔍 Digite o nome para buscar..."
                value={searchFuncionario}
                onChange={(e) => setSearchFuncionario(e.target.value)}
                style={{ width: '100%', padding: '8px', marginBottom: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
              <select 
                value={formData.funcionario_id} 
                onChange={(e) => setFormData({...formData, funcionario_id: e.target.value})}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px', fontSize: isMobile ? '13px' : '14px' }}
                size={Math.min(funcionariosFiltrados.length + 1, 5)}
              >
                <option value="">Selecione um funcionário...</option>
                {funcionariosFiltrados.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.nome_completo} - {f.cargo || 'Sem cargo'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Data de Saída *</label>
              <input type="date" value={formData.data_saida} onChange={(e) => setFormData({...formData, data_saida: e.target.value})} required style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
            </div>
            <div>
              <label>Data Aviso Prévio</label>
              <input type="date" value={formData.data_aviso_previo} onChange={(e) => setFormData({...formData, data_aviso_previo: e.target.value})} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
            </div>
            <div>
              <label>Motivo *</label>
              <select value={formData.motivo} onChange={(e) => setFormData({...formData, motivo: e.target.value})} required style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}>
                <option value="">Selecione...</option>
                <option value="DEMISSAO_SEM_JUSTA_CAUSA">Demissão sem Justa Causa</option>
                <option value="DEMISSAO_COM_JUSTA_CAUSA">Demissão com Justa Causa</option>
                <option value="PEDIDO_DEMISSAO">Pedido de Demissão</option>
                <option value="ACORDO">Acordo</option>
                <option value="FIM_CONTRATO">Fim de Contrato</option>
                <option value="APOSENTADORIA">Aposentadoria</option>
              </select>
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label>Observação</label>
              <textarea value={formData.observacao} onChange={(e) => setFormData({...formData, observacao: e.target.value})} rows="2" style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
            </div>
          </div>
          <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="submit" disabled={loading} style={{ padding: isMobile ? '8px 20px' : '10px 30px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px' }}>
              <FaCalculator /> {loading ? 'Calculando...' : 'Calcular Rescisão'}
            </button>
            <button type="button" onClick={() => { setMostrarForm(false); resetForm(); }} style={{ padding: isMobile ? '8px 20px' : '10px 30px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px' }}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* ============================================ */}
      {/* LISTA DE RESCISÕES - RESPONSIVA */}
      {/* ============================================ */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '10px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        {rescisoesFiltradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
            <p style={{ color: '#6c757d' }}>
              {searchTerm ? `🔍 Nenhuma rescisão encontrada para "${searchTerm}"` : 'Nenhuma rescisão registrada'}
            </p>
          </div>
        ) : isMobile ? (
          // ============================================
          // VISUALIZAÇÃO MOBILE - CARDS
          // ============================================
          <div>
            {rescisoesFiltradas.map(r => (
              <div
                key={r.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  padding: '15px',
                  marginBottom: '12px',
                  borderLeft: `4px solid ${getStatusColor(r.status)}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <strong style={{ fontSize: '16px' }}>{r.funcionario?.nome_completo || 'N/A'}</strong>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '12px',
                    backgroundColor: getStatusColor(r.status),
                    color: getStatusTextColor(r.status),
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {r.status}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', fontSize: '13px' }}>
                  <div><strong>Data Saída:</strong> {formatDate(r.data_saida)}</div>
                  <div><strong>Motivo:</strong> {getMotivoLabel(r.motivo)}</div>
                  <div><strong>Valor Líquido:</strong> <span style={{ fontWeight: 'bold', color: '#28a745' }}>{formatCurrency(r.total_liquido)}</span></div>
                </div>
                <div style={{ marginTop: '10px', display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
                  <button onClick={() => navigate(`/rescisao/${r.id}`)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer' }} title="Ver Detalhes">
                    <FaEye />
                  </button>
                  <button style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer' }} title="Imprimir">
                    <FaPrint />
                  </button>
                  {r.status === 'CALCULADO' && (
                    <button style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer' }} title="Confirmar Pagamento">
                      <FaCheck />
                    </button>
                  )}
                  {r.status === 'PENDENTE' && (
                    <button style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }} title="Excluir" onClick={() => handleDelete(r.id)}>
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // ============================================
          // VISUALIZAÇÃO DESKTOP - TABELA
          // ============================================
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>ID</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Funcionário</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Data Saída</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Motivo</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Total</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rescisoesFiltradas.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '10px' }}>{r.id}</td>
                  <td style={{ padding: '10px' }}>{r.funcionario?.nome_completo || '-'}</td>
                  <td style={{ padding: '10px' }}>{formatDate(r.data_saida)}</td>
                  <td style={{ padding: '10px' }}>{getMotivoLabel(r.motivo)}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#28a745' }}>
                    {formatCurrency(r.total_liquido)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: getStatusColor(r.status),
                      color: getStatusTextColor(r.status),
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {r.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <button onClick={() => navigate(`/rescisao/${r.id}`)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', marginRight: '8px' }} title="Ver Detalhes">
                      <FaEye />
                    </button>
                    <button style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer', marginRight: '8px' }} title="Imprimir">
                      <FaPrint />
                    </button>
                    {r.status === 'CALCULADO' && (
                      <button style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer', marginRight: '8px' }} title="Confirmar Pagamento">
                        <FaCheck />
                      </button>
                    )}
                    {r.status === 'PENDENTE' && (
                      <button style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }} title="Excluir" onClick={() => handleDelete(r.id)}>
                        <FaTrash />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Estilos adicionais */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
          }
          @media (max-width: 768px) {
            .table-responsive {
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Rescisoes;